import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/contexts/AuthContext';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { setEmailVerified } = useAuth();
  const email = localStorage.getItem('verifyEmail') || '';
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (!email && !verified) navigate('/register');
    inputsRef.current[0]?.focus();
  }, [email, navigate, verified]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleChange = (idx: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[idx] = value.slice(-1);
    setOtp(newOtp);
    if (value && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      inputsRef.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) return;
    setLoading(true);
    try {
      await api.post('/auth/verify-email', { email, otp: code });
      toast('success', 'Email verified successfully!');
      setVerified(true); // Prevent redirect to /register
      setEmailVerified();
      localStorage.removeItem('verifyEmail');
      // Force full page reload to pick up new auth state
      // Check subscription first, then redirect
      try {
        const { data: sub } = await api.get('/subscriptions/me');
        if (sub?.status === 'active') {
          window.location.href = '/dashboard';
        } else {
          window.location.href = '/choose-plan';
        }
      } catch {
        window.location.href = '/choose-plan';
      }
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await api.post('/auth/resend-verification', { email });
      toast('success', 'New verification code sent!');
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to resend code');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="h-14 w-14 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
            <Mail className="h-7 w-7 text-[#6366F1]" />
          </div>
          <h1 className="text-2xl font-bold text-[#1E293B]">Verify Your Email</h1>
          <p className="text-sm text-slate-500 mt-2 text-center">
            We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>
          </p>
        </div>

        <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={el => { inputsRef.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-200 rounded-xl focus:border-[#6366F1] focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-colors"
            />
          ))}
        </div>

        <button
          onClick={handleVerify}
          disabled={loading || otp.join('').length !== 6}
          className="w-full h-12 bg-[#6366F1] hover:bg-[#4F46E5] text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Verify Email
        </button>

        <div className="text-center mt-4">
          <p className="text-sm text-slate-500">
            Didn't receive the code?{' '}
            {resendCooldown > 0 ? (
              <span className="text-slate-400">Resend in {resendCooldown}s</span>
            ) : (
              <button onClick={handleResend} className="text-[#6366F1] font-medium hover:underline">
                Resend Code
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
